import React, { useRef, useState, useCallback } from 'react';
import { ImageIcon, Download, X, Upload } from 'lucide-react';

const TARGET_SIZE = 3000;

function AdminImageEditPage() {
  const [preview, setPreview]   = useState(null); // { src, name, w, h }
  const [output, setOutput]     = useState(null); // { url, name }
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const processFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setOutput(null);
    setPreview(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // off-screen canvas — 절대 DOM에 붙이지 않음
        const canvas = document.createElement('canvas');
        canvas.width  = TARGET_SIZE;
        canvas.height = TARGET_SIZE;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, TARGET_SIZE, TARGET_SIZE);

        // 비율 유지 리사이징 (전체 이미지 포함, 여백은 흰색)
        const ratio = Math.min(TARGET_SIZE / img.width, TARGET_SIZE / img.height);
        const dw    = img.width  * ratio;
        const dh    = img.height * ratio;
        const dx    = (TARGET_SIZE - dw) / 2;
        const dy    = (TARGET_SIZE - dh) / 2;
        ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, dw, dh);

        const ext      = (file.name.match(/\.([^.]+)$/) || ['', 'jpg'])[1].toLowerCase();
        const mime     = ext === 'png' ? 'image/png' : 'image/jpeg';
        const baseName = file.name.replace(/\.[^.]+$/, '');
        const outName  = `${baseName}_3000x3000.${ext === 'png' ? 'png' : 'jpg'}`;
        const thumbUrl = canvas.toDataURL('image/jpeg', 0.5); // 미리보기용

        setPreview({ src: e.target.result, name: file.name, w: img.width, h: img.height, thumbUrl });

        canvas.toBlob(
          (blob) => {
            const url = URL.createObjectURL(blob);
            setOutput({ url, name: outName });

            // 자동 다운로드
            const a = document.createElement('a');
            a.href = url;
            a.download = outName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          },
          mime,
          0.95,
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files[0]);
  }, [processFile]);

  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onFileChange = (e) => processFile(e.target.files[0]);

  const reset = () => {
    if (output) URL.revokeObjectURL(output.url);
    setPreview(null);
    setOutput(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center gap-2 mb-6">
        <ImageIcon size={20} className="text-amber-400" />
        <h1 className="text-lg font-bold text-white">이미지편집</h1>
      </div>

      {/* 섹션: 커버이미지 */}
      <div className="bg-white/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-semibold text-amber-300 tracking-wide">커버이미지</h2>
          <span className="text-xs text-slate-500">이미지를 넣으면 3,000 × 3,000 px 정방형으로 변환 후 자동 다운로드됩니다</span>
        </div>

        {!preview ? (
          /* 드롭존 */
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed cursor-pointer
              min-h-[280px] transition-colors select-none
              ${dragging
                ? 'border-amber-400 bg-amber-400/10'
                : 'border-white/15 hover:border-amber-400/50 hover:bg-white/5'}
            `}
          >
            <Upload size={40} className={dragging ? 'text-amber-400' : 'text-slate-500'} />
            <div className="text-center">
              <p className="text-sm text-slate-300 font-medium">이미지를 드래그하거나 클릭해서 선택</p>
              <p className="text-xs text-slate-500 mt-1">JPG · PNG · WEBP · GIF 지원</p>
            </div>
          </div>
        ) : (
          /* 결과 */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* 원본 */}
              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-medium">원본</p>
                <div className="rounded-xl overflow-hidden bg-white/5 aspect-square flex items-center justify-center">
                  <img src={preview.src} alt="original" className="w-full h-full object-contain" />
                </div>
                <p className="text-xs text-slate-500 text-center">{preview.w} × {preview.h} px</p>
              </div>

              {/* 변환 결과 */}
              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-medium">변환 결과 (3,000 × 3,000)</p>
                <div className="rounded-xl overflow-hidden bg-white/5 aspect-square flex items-center justify-center">
                  {preview.thumbUrl
                    ? <img src={preview.thumbUrl} alt="converted" className="w-full h-full object-contain" />
                    : <span className="text-slate-500 text-xs">처리 중…</span>
                  }
                </div>
                <p className="text-xs text-slate-500 text-center">3,000 × 3,000 px</p>
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="flex items-center gap-2 pt-1">
              {output && (
                <a
                  href={output.url}
                  download={output.name}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-sm font-medium transition-colors"
                >
                  <Download size={14} />
                  다시 다운로드
                </a>
              )}
              <button
                onClick={reset}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 text-sm transition-colors"
              >
                <X size={14} />
                다른 이미지
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFileChange}
        />
      </div>
    </div>
  );
}

export default AdminImageEditPage;
